import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Save,
  FileText,
  CheckCircle2,
  Circle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { intakeService } from '@/services/api/intake';
import { toast } from 'sonner';
import type { IntakeDocumentType } from '@/types/intake';

interface ClientDocumentSetting {
  documentTypeId: string;
  required: boolean;
  optional: boolean;
}

export function IntakeClientDocumentsPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Track which documents are selected and their requirement status
  const [documentSettings, setDocumentSettings] = useState<Map<string, { required: boolean; optional: boolean }>>(
    new Map()
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch client details
  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ['intake-client', clientId],
    queryFn: () => intakeService.getClient(clientId!),
    enabled: !!clientId,
  });

  // Fetch all document types
  const { data: documentTypesData, isLoading: docTypesLoading } = useQuery({
    queryKey: ['intake-document-types'],
    queryFn: () => intakeService.listDocumentTypes(),
  });

  // Fetch client's current document requirements
  const { data: clientDocsData, isLoading: clientDocsLoading } = useQuery({
    queryKey: ['intake-client-documents', clientId],
    queryFn: () => intakeService.getClientDocuments(clientId!),
    enabled: !!clientId,
  });

  // Initialize settings from existing client documents when data loads
  useEffect(() => {
    if (clientDocsData?.documents) {
      const settings = new Map<string, { required: boolean; optional: boolean }>();
      clientDocsData.documents.forEach((doc: { documentTypeId: string; isRequired: boolean }) => {
        settings.set(doc.documentTypeId, {
          required: doc.isRequired,
          optional: !doc.isRequired,
        });
      });
      setDocumentSettings(settings);
    }
  }, [clientDocsData]);

  const documentTypes = documentTypesData?.documentTypes || [];

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const requirements = Array.from(documentSettings.entries())
        .filter(([_, setting]) => setting.required || setting.optional)
        .map(([docTypeId, setting]) => ({
          documentTypeId: docTypeId,
          isRequired: setting.required,
        }));

      return intakeService.setClientDocumentRequirements(clientId!, requirements);
    },
    onSuccess: () => {
      toast.success('Document requirements saved successfully');
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['intake-client-documents', clientId] });
    },
    onError: () => {
      toast.error('Failed to save document requirements');
    },
  });

  const handleDocumentToggle = (docTypeId: string, type: 'required' | 'optional') => {
    setDocumentSettings((prev) => {
      const newSettings = new Map(prev);
      const current = newSettings.get(docTypeId) || { required: false, optional: false };

      if (type === 'required') {
        // If selecting required, unselect optional
        newSettings.set(docTypeId, { required: !current.required, optional: false });
      } else {
        // If selecting optional, unselect required
        newSettings.set(docTypeId, { required: false, optional: !current.optional });
      }

      // If both are false, remove from map
      const updated = newSettings.get(docTypeId)!;
      if (!updated.required && !updated.optional) {
        newSettings.delete(docTypeId);
      }

      return newSettings;
    });
    setHasChanges(true);
  };

  const isLoading = clientLoading || docTypesLoading || clientDocsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold">Client not found</h2>
        <p className="text-muted-foreground">The requested client does not exist.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/panel/intake/clients')}>
          Back to Clients
        </Button>
      </div>
    );
  }

  const selectedCount = documentSettings.size;
  const requiredCount = Array.from(documentSettings.values()).filter((s) => s.required).length;
  const optionalCount = Array.from(documentSettings.values()).filter((s) => s.optional).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/panel/intake/clients')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Set Documents</h1>
            <p className="text-muted-foreground">
              Configure required documents for <span className="font-medium">{client.name}</span>
            </p>
          </div>
        </div>
        <Button onClick={() => saveMutation.mutate()} disabled={!hasChanges || saveMutation.isPending}>
          {saveMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Summary */}
      <div className="flex gap-4">
        <Card className="flex-1">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{selectedCount}</p>
                <p className="text-sm text-muted-foreground">Documents Selected</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="flex-1">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{requiredCount}</p>
                <p className="text-sm text-muted-foreground">Required</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="flex-1">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Circle className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{optionalCount}</p>
                <p className="text-sm text-muted-foreground">Optional</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Document List */}
      <Card>
        <CardHeader>
          <CardTitle>Document Types</CardTitle>
          <CardDescription>
            Select which documents this client needs to submit. Mark them as required or optional.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {documentTypes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No document types configured yet.</p>
              <Button
                variant="link"
                className="mt-2"
                onClick={() => navigate('/panel/intake/document-types')}
              >
                Configure Document Types
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {documentTypes.map((docType: IntakeDocumentType) => {
                const setting = documentSettings.get(docType.id) || { required: false, optional: false };
                const isSelected = setting.required || setting.optional;

                return (
                  <div
                    key={docType.id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      isSelected ? 'bg-muted/50 border-primary/30' : 'hover:bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-primary/20' : 'bg-muted'
                        }`}
                      >
                        <FileText className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <p className="font-medium">{docType.name}</p>
                        {docType.description && (
                          <p className="text-sm text-muted-foreground">{docType.description}</p>
                        )}
                        {docType.category && (
                          <Badge variant="outline" className="mt-1 text-xs">
                            {docType.category}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`required-${docType.id}`}
                          checked={setting.required}
                          onCheckedChange={() => handleDocumentToggle(docType.id, 'required')}
                        />
                        <Label
                          htmlFor={`required-${docType.id}`}
                          className="text-sm font-medium cursor-pointer text-red-600 dark:text-red-400"
                        >
                          Required
                        </Label>
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`optional-${docType.id}`}
                          checked={setting.optional}
                          onCheckedChange={() => handleDocumentToggle(docType.id, 'optional')}
                        />
                        <Label
                          htmlFor={`optional-${docType.id}`}
                          className="text-sm font-medium cursor-pointer text-blue-600 dark:text-blue-400"
                        >
                          Optional
                        </Label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-2 flex items-center gap-3 shadow-lg">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <span className="text-sm text-yellow-600 dark:text-yellow-400">You have unsaved changes</span>
          <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Now'}
          </Button>
        </div>
      )}
    </div>
  );
}
